import {
  AgentAIConfig,
  SocketConnectionConfig,
  MessagePayload,
  ConnectionState,
} from "../types";
import {
  getStorageItem,
  hasStorage,
  setStorageItem,
  removeStorageItem,
} from "../utils/storage";
import { clientBus, internalBus } from "../utils/eventBus";
import { INTERNAL_EVENTS } from "../constants/eventConsts";
import KoreGenerateuuidPipe from "../utils/uuid";
import { Socket } from "socket.io-client";
import { websocketService } from "./socketServices";
import { AgentAIApi } from "./api";

export class RootService {
  internalTransferData: any = null;
  config: AgentAIConfig = {} as AgentAIConfig;
  aaSettings: any = null;
  aaPreferences: any = null;
  _isValid: boolean = true;
  nameSpacePath: string = "koreagentassist";

  isWelcomeMessageSent: boolean = true;
  agentUserDetails: any = {};
  crId: string = "";
  maxIntermittentSummaryCount: number = 2;
  isIntermittentSummaryEnabled: boolean = false;
  generatedIntermittentSummaryCount: number = 0;
  isSummarizationEnabled: boolean = false;
  isInternalTransferInitialized: boolean = false;
  isAnonymousFlowEnabled: boolean = false;
  socket: Socket | null = null;
  api!: AgentAIApi;
  webSocketService!: websocketService;

  constructor(config: AgentAIConfig) {
    this.config = config;
    this.loadAPICall();
  }

  loadAPICall() {
    const validationError = this.validateConfig(this.config);
    if (validationError) {
      this._isValid = false;
      queueMicrotask(() => {
        clientBus.emitError(validationError);
      });
      return;
    }
    this.webSocketService = new websocketService();
    this.webSocketService.config = this.config;
    this.nameSpacePath = this.config?.connection?.headLess
      ? "agentaiheadless"
      : "koreagentassist";
    this.api = new AgentAIApi({
      ...this.config.connection,
    });
    this.api
      .authenticate({
        source: "desktop",
        conversationId: this.config.connection.conversationId,
        isSmartAssist: false,
        experience: this.config.connection.channel || "chat",
      })
      .then((response) => {
        if (response) {
          this.api.setAuthInfo({
            accessToken: response?.authorization?.accessToken,
            accountId: response?.userInfo?.accountId,
            invokeNewAAService: response?.invokeNewAAService,
            userId: response?.userInfo?.userId,
          });
          if (response?.isWidgetDisabled) {
            clientBus.emitError("AgentAI Widget is disabled");
            return;
          }
          this.isAnonymousFlowEnabled =
            response?.isAnonymousFlowEnabled || false;
          this.socketConnection();
        }
      })
      .catch((error) => {
        clientBus.emitError("Failed to authenticate", error);
      });
  }

  setupInternalEventListeners(): void {
    internalBus.on(INTERNAL_EVENTS.AGENTAI_READY, (data: any) => {
      this.handleOnReadyEvent(data);
    });

    internalBus.on(INTERNAL_EVENTS.INTERNAL_TRANSFER_RESPONSE, (data: any) => {
      this.handleInternalTransferResponse(data);
    });

    internalBus.on(
      INTERNAL_EVENTS.RESPONSE_RESOLUTION_COMMENTS,
      (data: any) => {
        this.handleResponseResolutionComments(data);
      },
    );

    internalBus.on(INTERNAL_EVENTS.CONV_META_INFO, (data: any) => {
      this.handleConvMetaInfo(data);
    });

    internalBus.on(INTERNAL_EVENTS.WELCOME_MESSAGE_SENT, (data: any) => {
      this.handleWelcomeMessageSent(data);
    });
  }

  socketConnection(): void {
    this.webSocketService.socketConnection(
      this.api,
      this.nameSpacePath,
    );
    const socket = this.webSocketService.getSocket();
    if (socket) {
      this.socket = socket;
    }
    this.setupInternalEventListeners();
  }

  /**
   * Validate the configuration
   * @returns Error message if validation fails, null if valid
   */
  validateConfig(config: AgentAIConfig): string | null {
    if (!config) {
      return "Configuration is required";
    }

    if (!config.connection) {
      return "Connection configuration is required";
    }

    const { connection } = config;
    const requiredFields: (keyof SocketConnectionConfig)[] = [
      "conversationId",
      "botId",
      "domainURL",
      "token",
      "interactiveLanguage",
      "customData",
      "channel",
    ];

    for (const field of requiredFields) {
      if (!connection[field]) {
        return `${field} is required in connection config`;
      }
    }

    return null;
  }

  handleInternalTransferResponse(response: any): void {
    if (!response) {
      clientBus.emitError("Internal transfer response is required");
      return;
    }
    if (response && response?.requestType === "internal_transfer_via_api") {
      let internalTransferData = this.internalTransferData;
      const InternalTransferDataLength =
        Object.keys(this.internalTransferData || {})?.length ||
        Object.keys(
          getStorageItem(`${this.config?.connection?.conversationId}`, true) ||
            {},
        ).length;

      if (!internalTransferData && InternalTransferDataLength > 0) {
        internalTransferData = getStorageItem(
          `${this.config?.connection?.conversationId}`,
          true,
        );
      }

      if (!InternalTransferDataLength) {
        this.internalTransfer(response);
        return;
      }

      if (InternalTransferDataLength && internalTransferData?.isAnonymous) {
        this.internalTransfer(response);
        return;
      }

      if (
        InternalTransferDataLength &&
        internalTransferData?.participant?.identity ===
          response?.participant?.identity
      ) {
        this.internalTransfer(response);
        return;
      }

      if (
        InternalTransferDataLength &&
        internalTransferData?.participant?.participantId ===
          response?.participant?.identity
      ) {
        this.internalTransfer(response);
        return;
      }
    } else {
      if (
        response?.isAnonymous &&
        response?.participant?.identity ===
          this.internalTransferData?.participant?.identity
      ) {
        this.internalTransferData.participant = { ...response?.participant };
        this.checkAndUpdateDataInLocalStorage(
          JSON.parse(JSON.stringify(this.internalTransferData)),
        );
      }
    }
  }

  checkAndUpdateDataInLocalStorage(data: any): void {
    if (!hasStorage()) {
      return;
    }
    const key = `${this.config?.connection?.conversationId}`;
    setStorageItem(key, data);
  }

  handleResponseResolutionComments(event: any): void {
    if (event?.type === "interim") {
      this.generatedIntermittentSummaryCount++;
    }
    const key = `${this.config?.connection?.conversationId}`;
    const lsData: any = getStorageItem(key, true);
    if (lsData && event?.type === "interim") {
      lsData.generatedIntermittentSummaryCount =
        this.generatedIntermittentSummaryCount;
      setStorageItem(key, lsData);
    }
  }

  handleConvMetaInfo(event: any): void {
    this.crId = event?.crId || "";
  }

  handleWelcomeMessageSent(value: boolean): void {
    this.isWelcomeMessageSent = value;
  }

  handleOnReadyEvent(data: any): void {
    this.aaSettings = data?.aa_settings?.agentAssistSettings;
    this.aaPreferences = data?.aa_prefs;
    const imSGroup = data?.aa_prefs?.prefs?.[
      this.config?.connection?.channel || ""
    ]?.locale?.[
      this.config?.connection?.interactiveLanguage || "en"
    ]?.act?.find((item: any) => item?.imS);
    if (imSGroup && imSGroup?.imS) {
      this.isIntermittentSummaryEnabled = imSGroup?.imS?.isE || false;
      this.maxIntermittentSummaryCount = imSGroup?.imS?.sCnt || 2;
    }

    const channel = this.config?.connection?.channel || "";
    const language = this.config?.connection?.interactiveLanguage || "en";
    this.isSummarizationEnabled =
      this.aaSettings?.[channel]?.summarization?.localeData?.[language]
        ?.isEnabled || false;
    this.sendWelcomeMessageRequest();
    if (getStorageItem(`${this.config?.connection?.conversationId}`)) {
      this.isInternalTransferInitialized = true;
      this.internalTransferData = getStorageItem(
        `${this.config?.connection?.conversationId}`,
        true,
      );
      this.generatedIntermittentSummaryCount =
        this.internalTransferData?.generatedIntermittentSummaryCount || 0;
    }
    if (data?.internal_transfer_response) {
      this.handleInternalTransferResponse(data?.internal_transfer_response);
    } else {
      if (!this.isInternalTransferInitialized) {
        this.sendTheAnonymousInternalTransfer();
      }
    }
  }

  sendWelcomeMessageRequest(): void {
    if (!this.socket) return;
    let welcomeMessageParams: any = {
      waitTime: 2000,
      userName: this.config?.connection?.userName || "",
      id: this.config?.connection?.conversationId || "",
      isSendWelcomeMessage: this.isWelcomeMessageSent,
      agentassistInfo: this.agentUserDetails,
      botId: this.config?.connection?.botId || "",
      sendMenuRequest: true,
      uId: this.config?.connection?.userId || "",
      sId: this.config?.connection?.sessionId || "",
      experience: this.config?.connection?.channel,
      jToken: this.config?.connection?.token,
      conversationId: this.config?.connection?.conversationId || "",
      customData: this.config?.connection?.customData || {},
      autoBotId: this.config?.connection?.autoBotId || "",
      language: this.config?.connection?.interactiveLanguage || "",
      allQueryParams: { ...(this.config?.connection || {}) },
    };

    this.webSocketService.emitSocketEvent(
      "welcome_message_request",
      welcomeMessageParams,
    );
  }

  sendTheAnonymousInternalTransfer(): void {
    if (this.isAnonymousFlowEnabled) {
      const anonymousId = KoreGenerateuuidPipe("an");
      const payload = {
        participant: {
          identity: anonymousId,
          name: "Anonymous",
          type: "agent",
        },
        transition: "entry",
        transfertype: "NA",
        isAnonymous: true,
      };
      this.internalTransfer(payload, true);
    }
  }

  internalTransfer(payload: any, isAnonymous: boolean = false): boolean {
    if (!this.isConnected()) {
      clientBus.emitError("Socket is not connected. Call connect() first.");
      return false;
    }
    if (!payload.participant || !payload.transition || !payload.transfertype) {
      clientBus.emitError(
        "Participant, transition and transfertype are required",
      );
      return false;
    }
    try {
      if (!isAnonymous) {
        const LsinternalTransferData: any = getStorageItem(
          `${this.config?.connection?.conversationId}`,
          true,
        );
        const instanceInternalTransferData = this.internalTransferData;
        if (
          instanceInternalTransferData ||
          Object.keys(LsinternalTransferData || {}).length > 0
        ) {
          const anonymousId =
            instanceInternalTransferData?.participant?.identity ||
            LsinternalTransferData?.participant?.identity;
          const incomingParticipant = payload?.participant || {};
          if (payload?.transition === "exit") {
            payload = {
              ...payload,
              ...(instanceInternalTransferData || LsinternalTransferData),
              transition: payload?.transition,
              transfertype: payload?.transfertype,
            };

            payload.participant = {
              ...incomingParticipant,
              identity: incomingParticipant?.identity
                ? incomingParticipant?.identity
                : anonymousId,
              ...(!incomingParticipant?.identity && {
                participantId: incomingParticipant?.identity,
              }),
            };
          } else {
            payload = {
              ...payload,
              ...(instanceInternalTransferData || LsinternalTransferData),
              transition: payload?.transition,
              transfertype: payload?.transfertype,
            };

            payload.participant = {
              ...incomingParticipant,
              identity: anonymousId,
              participantId: incomingParticipant?.identity,
            };
          }
        }
        payload.isAnonymous = false;
      }
      this.internalTransferData = {
        ...payload,
        conversationId: this.config?.connection?.conversationId,
      };
      this.checkAndUpdateDataInLocalStorage(
        JSON.parse(JSON.stringify(this.internalTransferData)),
      );
      if (!this.socket) {
        clientBus.emitError("Socket is not set");
        return false;
      }
      this.webSocketService.emitSocketEvent(
        "internal_transfer",
        payload,
      );
    } catch (error) {
      clientBus.emitError("Failed to internal transfer", error);
      return false;
    }
    return true;
  }

  generateIntermittentSummary(isEndOfConversation: boolean = false): boolean {
    if (!this.isConnected()) {
      clientBus.emitError("Socket is not connected. Call connect() first.");
      return false;
    }
    if (!isEndOfConversation) {
      if (!this.isIntermittentSummaryEnabled || !this.isSummarizationEnabled) {
        clientBus.emitError("Intermittent summary is not enabled");
        return false;
      }
      if (
        this.generatedIntermittentSummaryCount >=
        this.maxIntermittentSummaryCount
      ) {
        clientBus.emitError("Max intermittent summary count reached");
        return false;
      }
    }
    if (!this.socket) {
      clientBus.emitError("Socket is not set");
      return false;
    }
    try {
      const channel = this.config?.connection?.channel || "";
      const language = this.config?.connection?.interactiveLanguage || "en";
      const payload = {
        sessionId: this.config?.connection?.sessionId,
        type: isEndOfConversation ? "final" : "interim",
        interimSummary: true,
        userId: this.api?.getUserId() || "",
        model:
          this.aaSettings?.[channel]?.summarization?.localeData?.[language]
            ?.model || {},
        crId: this.crId || "",
        numberOfGeneratedSummaries: this.generatedIntermittentSummaryCount,
      };

      if (isEndOfConversation) {
        if (this.isSummarizationEnabled) {
          this.webSocketService.emitSocketEvent(
            "request_resolution_comments",
            payload,
          );
        }
        this.webSocketService.emitSocketEvent(
          "end_of_conversation",
          payload,
        );
        removeStorageItem(`${this.config?.connection?.conversationId}`);
      } else {
        this.webSocketService.emitSocketEvent(
          "request_resolution_comments",
          payload,
        );
      }
      return true;
    } catch (error) {
      clientBus.emitError("Failed to generate intermittent summary", error);
      return false;
    }
  }

  endOfConversation(): boolean {
    if (!this.isConnected()) {
      clientBus.emitError("Socket is not connected. Call connect() first.");
      return false;
    }
    try {
      const isSuccess = this.generateIntermittentSummary(true);
      return isSuccess;
    } catch (error) {
      clientBus.emitError("Failed to end of conversation", error);
      return false;
    }
  }

  sendAgentMessage(payload: MessagePayload): boolean {
    if (!this.isConnected()) {
      clientBus.emitError("Socket is not connected. Call connect() first.");
      return false;
    }
    if (!payload.message || payload.message.trim() === "") {
      clientBus.emitError(
        "Message content is required and author must be agent",
      );
      return false;
    }
    if (!this.socket) {
      clientBus.emitError("Socket is not set");
      return false;
    }
    const payloadWithAuthor = {
      author: {
        type: "AGENT",
      },
      value: payload.message,
      message: payload.message,
    };
    this.webSocketService.emitSocketEvent(
      "agent_sent_message",
      payloadWithAuthor,
    );
    return true;
  }

  sendUserMessage(payload: MessagePayload): boolean {
    if (!this.isConnected()) {
      clientBus.emitError("Socket is not connected. Call connect() first.");
      return false;
    }
    if (!payload.message || payload.message.trim() === "") {
      clientBus.emitError(
        "Message content is required and author must be user",
      );
      return false;
    }
    if (!this.socket) {
      clientBus.emitError("Socket is not set");
      return false;
    }
    const payloadWithAuthor = {
      author: {
        type: "USER",
      },
      value: payload.message,
      message: payload.message,
    };
    this.webSocketService.emitSocketEvent(
      "user_sent_message",
      payloadWithAuthor,
    );
    return true;
  }

  disconnect(): void {
    this.webSocketService.disconnect();
  }

  getConnectionState(): ConnectionState {
    return this.webSocketService.connectionState;
  }

  isConnected(): boolean {
    return this.webSocketService.isConnected();
  }

  getReconnectAttempts(): number {
    return this.webSocketService._reconnectAttempts;
  }

  getSocket(): Socket | null {
    return this.webSocketService.getSocket();
  }

  isValid(): boolean {
    return this._isValid;
  }
}
